import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import {
  AppShellComponent,
  IconComponent,
  LanguageSelectorComponent,
} from 'continity-shared-ui';
import { AuthService } from 'continity-shared-auth';
import { ShellThemeService } from './shell-theme.service';
import { ShellProduct } from './shell-product';
import { ProductSwitcherComponent } from './product-switcher.component';

/**
 * Continity shared shell frame (`continity-shared-shell`, PBI 1463 / ADO #1522).
 *
 * Wraps the shared-ui `AppShellComponent` (resizable sidebar + header + content slots)
 * with the cross-app concerns: auth-ready gating (`auth.authReady$ | async`) and a
 * lightweight language switch in `header-actions`. The app projects its own nav via
 * `<… slot="sidebar-nav">` (nav stays app-specific) and the router-outlet renders in
 * the main content slot.
 *
 * Extracted from the Phoenix-Portal-App's local app-shell composition with the INTENT
 * that several portals share one shell. As of today (2026-06) the shell has exactly ONE
 * real consumer — the Phoenix app. The MAIN portal still renders its OWN hand-rolled
 * `main-layout`/`main-sidebar`/`main-header` and is NOT on this shell.
 *
 * What the main portal ACTUALLY shares from `continity-shared-shell` is narrow: the
 * `TokenStorageService` (re-exported by the portal's `token-storage.service.ts`) and the
 * `ShellProduct` type for its product switcher. It does NOT consume the visual frame or
 * the `provideShell` seams (auth probe, config, interceptor, AUTH_* tokens) — those are
 * Phoenix-only. So "shared shell" is currently aspirational, not realised.
 *
 * NOTE: `cs-shell` does NOT expose rich-frame slot parity (sidebar sections / header
 * areas / overlays / a logo-adjacent slot) — out of scope for Stage 0. A rich-frame app
 * like the main portal cannot migrate onto it without that parity (e.g. there is no slot
 * beside the logo — which is why the product switcher sits next to the logo in the main
 * portal but in the sidebar-nav in Phoenix).
 *
 * Follow-up (cross-portal shell platform) — ADO Feature #1607: pull the auth/SSO seam out
 * of the visual shell into a standalone lib (#1608), bring the shell to rich-frame parity
 * (#1609), then migrate the main portal LAST (#1610). NB: #1522 is only the (Phoenix-side)
 * extraction task, NOT the main-portal migration.
 */
@Component({
  selector: 'cs-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    AppShellComponent,
    ProductSwitcherComponent,
    LanguageSelectorComponent,
    IconComponent,
  ],
  template: `
    <ng-container *ngIf="auth.authReady$ | async; else loading">
      <app-shell
        [appTitle]="appTitle"
        [appDescription]="appDescription"
        [storageKey]="storageKey"
        [logoLight]="logoLight"
        [logoDark]="logoDark"
        [logoCollapsed]="logoCollapsed"
        [logoAlt]="logoAlt"
        [logoHref]="logoHref">

        <div slot="sidebar-nav">
          <!-- Cross-portal product switcher (922 Entscheidung 8, Option B — ADO #1543).
               Compact popup-button form. Declared here (inside the sidebar-nav slot) but
               started hidden (display:none) and RELOCATED at runtime into the app-shell
               logo row by ngAfterViewInit → relocateSwitcher(), so it lands in the same
               logo corner as the main portal WITHOUT a continity-shared-ui edit. See the
               HACK note on relocateSwitcher(). Labels are config-driven, NOT i18n'd (only
               the trigger tooltip is). -->
          <cs-product-switcher
            style="display: none;"
            [products]="products"
            [triggerLabel]="switchProductLabel"></cs-product-switcher>

          <ng-content select="[slot=sidebar-nav]"></ng-content>
        </div>

        <div slot="header-actions" class="flex items-center gap-2">
          <!-- Same language dropdown as the main portal: the shared-ui
               app-language-selector, fed by the lean ShellLanguageService bound to the
               LANGUAGE_SERVICE token in provideShell(). No portal LanguageService
               coupling and no shared-ui change — we only provide the extension token. -->
          <app-language-selector class="flex items-center"></app-language-selector>

          <!-- Dark-mode toggle. Lean ShellThemeService flips the 'dark' class on the
               root element (shared-ui Tailwind preset is darkMode:'class'); mirrors the
               main portal's sun/moon button without the portal's ThemeService. -->
          <button type="button" class="cs-shell-theme-btn"
            (click)="theme.toggle()"
            [attr.aria-label]="themeToggleLabel" [attr.title]="themeToggleLabel"
            [attr.data-cy]="langCyPrefix + '-theme-toggle'">
            <app-icon [name]="(theme.isDarkMode$ | async) ? 'sun' : 'moon'" [size]="20"></app-icon>
          </button>

          <!-- Signed-in user + tenant + logout. Makes the otherwise-invisible SSO session
               and tenant binding explicit. Hidden when no user (probe-only / signed out). -->
          <ng-container *ngIf="auth.currentUser$ | async as user">
            <div class="cs-shell-user" data-cy="cs-shell-user">
              <app-icon name="user" [size]="16"></app-icon>
              <span class="cs-shell-user-text">
                <span class="cs-shell-user-name">{{ user.firstName }} {{ user.lastName }}</span>
                <span class="cs-shell-user-tenant">{{ user.companyName }}</span>
              </span>
            </div>
            <button type="button" class="cs-shell-logout-btn"
              (click)="auth.logout()"
              [attr.aria-label]="logoutLabel" [attr.title]="logoutLabel"
              [attr.data-cy]="langCyPrefix + '-logout'">
              {{ logoutLabel }}
            </button>
          </ng-container>
        </div>

        <main slot="" class="p-4">
          <router-outlet></router-outlet>
        </main>
      </app-shell>
    </ng-container>

    <ng-template #loading>
      <div [attr.data-cy]="langCyPrefix + '-auth-loading'"
        class="flex items-center justify-center h-screen w-full">
        <span>{{ authenticatingText }}</span>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .cs-shell-theme-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.4rem;
        border-radius: 0.375rem;
        color: inherit;
        cursor: pointer;
      }
      .cs-shell-theme-btn:hover {
        background: rgba(127, 127, 127, 0.12);
      }
      .cs-shell-user {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        padding-left: 0.5rem;
        margin-left: 0.25rem;
        border-left: 1px solid rgba(127, 127, 127, 0.25);
        min-width: 0;
      }
      .cs-shell-user-text {
        display: flex;
        flex-direction: column;
        line-height: 1.1;
        min-width: 0;
      }
      .cs-shell-user-name {
        font-size: 0.8rem;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .cs-shell-user-tenant {
        font-size: 0.7rem;
        opacity: 0.7;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .cs-shell-logout-btn {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
        border-radius: 0.375rem;
        color: inherit;
        cursor: pointer;
        opacity: 0.75;
      }
      .cs-shell-logout-btn:hover {
        background: rgba(127, 127, 127, 0.12);
        opacity: 1;
      }
    `,
  ],
})
export class ShellComponent implements AfterViewInit, OnDestroy {
  /** App title shown in the shell header (already-translated). */
  @Input() appTitle = '';
  /** App description shown in the shell header (already-translated). */
  @Input() appDescription = '';
  /** Persistence key for the resizable sidebar width. */
  @Input() storageKey = '';
  /** Text shown while the initial auth probe is in flight (already-translated). */
  @Input() authenticatingText = '';
  /** Accessible label/tooltip for the dark-mode toggle (already-translated). */
  @Input() themeToggleLabel = '';
  /** Label/text for the logout button (already-translated). */
  @Input() logoutLabel = '';
  /**
   * Prefix for the `data-cy` hooks so each app keeps its own selectors
   * (`<prefix>-auth-loading`, `<prefix>-theme-toggle`). Defaults to `phoenix`.
   */
  @Input() langCyPrefix = 'phoenix';
  /**
   * Cross-portal product entries (922 Entscheidung 8, Option B — ADO #1543). Rendered as
   * the compact popup-button switcher, relocated into the logo row at runtime. Empty →
   * the switcher renders nothing.
   */
  @Input() products: ShellProduct[] = [];
  /** Already-translated tooltip/aria-label for the product-switcher trigger button. */
  @Input() switchProductLabel = '';

  /**
   * Top-left sidebar logo (shared-ui `app-shell`). Expanded sidebar shows
   * {@link logoLight} (light theme) / {@link logoDark} (dark theme); collapsed sidebar
   * shows {@link logoCollapsed} forced to a 40×40 box → MUST be square. shared-ui's
   * defaults point at main-portal asset paths, so each consuming app passes its own.
   */
  @Input() logoLight = '';
  @Input() logoDark = '';
  @Input() logoCollapsed = '';
  @Input() logoAlt = '';
  @Input() logoHref = '/';

  /** Set once the switcher has been moved into the logo row (idempotency guard). */
  private relocated = false;
  /** Watches for the async-rendered app-shell so we can relocate once it appears. */
  private domObs?: MutationObserver;
  /** Hides the switcher when the sidebar collapses to the icon rail. */
  private collapseObs?: ResizeObserver;

  constructor(
    public auth: AuthService,
    public theme: ShellThemeService,
    private hostEl: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    // app-shell is gated behind the async auth probe (`auth.authReady$ | async`), so on the
    // first tick it may not be in the DOM yet. Try now; if it isn't ready, watch for it.
    if (this.relocateSwitcher()) return;
    this.domObs = new MutationObserver(() => {
      if (this.relocateSwitcher()) {
        this.domObs?.disconnect();
        this.domObs = undefined;
      }
    });
    this.domObs.observe(this.hostEl.nativeElement, { childList: true, subtree: true });
  }

  ngOnDestroy(): void {
    this.domObs?.disconnect();
    this.collapseObs?.disconnect();
  }

  /**
   * HACK (no continity-shared-ui edit): the shared-ui `app-shell` exposes NO slot beside
   * the logo (its logo row is a fixed `<div class="h-16 … justify-between"><a><img/></a>`
   * with no `ng-content`). To put the product switcher in the exact same place as the main
   * portal — the top-left logo corner — we physically MOVE our already-rendered
   * `<cs-product-switcher>` node from the sidebar-nav slot into that logo row at runtime.
   *
   * Fully reversible, lives entirely in our workspace (`continity-shared-shell`). The
   * proper fix is a logo-adjacent slot in `app-shell` — that's the rich-frame parity work
   * already tracked as ADO Feature #1607 / #1609 (see this class's header doc); replace
   * this hack when that lands. Re-verify after any `continity-shared-ui` app-shell upgrade.
   *
   * Returns true once the relocation succeeded (anchors found), false while not yet ready.
   */
  private relocateSwitcher(): boolean {
    if (this.relocated) return true;
    const root = this.hostEl.nativeElement;
    const switcher = root.querySelector<HTMLElement>('cs-product-switcher');
    // Logo anchor = the `a[href=logoHref]` that actually contains the logo <img>. The href
    // filter alone is ambiguous (the Phoenix nav also has an `a[href="/board"]`), so we
    // additionally require an <img> child — only the logo anchor has one (nav uses SVGs).
    const logoAnchor = Array.from(
      root.querySelectorAll<HTMLAnchorElement>(`a[href="${this.logoHref}"]`),
    ).find((a) => a.querySelector('img'));
    const logoRow = logoAnchor?.parentElement ?? null;
    if (!switcher || !logoRow) return false;

    // The logo <a> is `w-full`, so it eats the row's flex space — rather than fight it,
    // absolute-position the switcher at the right edge of the (now relative) logo row.
    this.renderer.setStyle(logoRow, 'position', 'relative');
    this.renderer.appendChild(logoRow, switcher);
    this.renderer.setStyle(switcher, 'position', 'absolute');
    this.renderer.setStyle(switcher, 'right', '0.5rem');
    this.renderer.setStyle(switcher, 'top', '50%');
    this.renderer.setStyle(switcher, 'transform', 'translateY(-50%)');
    this.renderer.setStyle(switcher, 'z-index', '60');

    // Collapse-aware: hide on the icon rail (mirrors the main portal's `*ngIf="!isCollapsed"`).
    // app-shell's internal isCollapsed() signal isn't reachable, so infer from the logo
    // row's width — the collapsed rail is ~64px, the expanded sidebar is ≥256px.
    this.collapseObs = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      this.renderer.setStyle(switcher, 'display', width < 100 ? 'none' : 'block');
    });
    this.collapseObs.observe(logoRow);

    this.relocated = true;
    return true;
  }
}
